import { ComponentType, MouseEvent, SVGProps, useCallback, useEffect, useRef } from 'react';
// Imported from the `solid` subpath, not the package root. The root barrel is what
// `useDynamicIcon` pulls in via `import()`, and a module cannot be both statically and lazily
// loaded — importing it here collapsed the lazy icon chunk into the main bundle. The root barrel
// only re-exports `regular/*`, so `solid` is a separate graph: these two glyphs tree-shake out of it
// and the dynamic chunk stays intact.
import { CheckCircle as CheckCircleSolid, XmarkCircle as XmarkCircleSolid } from 'iconoir-react/solid';
import { cn } from '@/lib/core/utils/cn';

/** Shape of an iconoir icon component, as used for the toast glyphs. */
type ToastIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface ToastProps {
  open: boolean;
  message: string;
  /** 'dark'/'light' are neutral (success/info); 'error' has its own dedicated colour. @default 'dark' */
  variant?: 'dark' | 'light' | 'error';
  /** Overrides the variant's default glyph. Pass an icon component, not a name. */
  icon?: ToastIcon;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss: () => void;
  /** Auto-dismiss timeout in ms. Defaults to 4000 (WCAG SC 2.2.1). */
  autoDismissMs?: number;
}

/*
 * Icons are imported statically rather than through the dynamic `<Icon>` loader: a toast only ever
 * draws one of two glyphs, so resolving them at runtime meant pulling the whole icon module (a
 * ~2.2MB chunk) to render a check mark — and the icon was missing until it downloaded. `iconoir-react`
 * sets `"sideEffects": false`, so these named imports tree-shake to just these two components.
 */
/** The xmark glyph, re-exported so callers can keep it on a non-error variant. */
export { XmarkCircleSolid as ToastErrorIcon };

const VARIANT_STYLES: Record<
  NonNullable<ToastProps['variant']>,
  { container: string; text: string; icon: ToastIcon }
> = {
  dark: { container: 'bg-jncquoi-neutral-1000', text: 'text-jncquoi-neutral-00', icon: CheckCircleSolid },
  light: {
    container: 'bg-jncquoi-neutral-00 border-jncquoi-neutral-200 border',
    text: 'text-jncquoi-neutral-1000',
    icon: CheckCircleSolid,
  },
  // No semantic token maps to this colour — same literal used by `SoldOutBadge` for
  // negative states ("Foundation/Asia/7427c-500").
  error: { container: 'bg-[#971b2f]', text: 'text-jncquoi-neutral-00', icon: XmarkCircleSolid },
};

/**
 * Generic bottom toast — fixed to the bottom of the screen, auto-dismisses, and optionally
 * exposes a single action button. Domain-agnostic; any flow can import it directly.
 */
export function Toast({
  open,
  message,
  variant = 'dark',
  icon,
  actionLabel,
  onAction,
  onDismiss,
  autoDismissMs = 4000,
}: ToastProps) {
  const styles = VARIANT_STYLES[variant];
  const IconComponent = icon ?? styles.icon;

  // Callers pass `onDismiss` as an inline arrow, so its identity changes on every render. Holding it
  // in a ref keeps it out of the timer effect's deps — otherwise each re-render cleared the pending
  // timeout and started a new one, and a screen that re-rendered kept the toast up indefinitely.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  const handleAction = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // The container dismisses on click, so stop this from also counting as a plain dismissal.
      event.stopPropagation();
      onAction?.();
      onDismissRef.current();
    },
    [onAction]
  );

  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => onDismissRef.current(), autoDismissMs);
    return () => window.clearTimeout(handle);
  }, [open, autoDismissMs]);

  if (!open) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // Tapping the toast dismisses it rather than forcing the user to wait out the timer. The
      // action button stops propagation so it still runs its own handler.
      onClick={() => onDismissRef.current()}
      className={cn(
        'fixed right-4 left-4 z-90 flex min-h-17 cursor-pointer items-center gap-3 rounded-[2px] p-4 shadow-lg',
        'bottom-[max(1rem,env(safe-area-inset-bottom))]',
        styles.container
      )}
    >
      <span className={cn('flex size-5 shrink-0 items-center justify-center', styles.text)}>
        <IconComponent width={20} height={20} />
      </span>
      <p className={cn('font-pp-neue-montreal flex-1 text-sm font-normal tracking-wide', styles.text)}>
        {message}
      </p>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={handleAction}
          className={cn(
            'font-pp-neue-montreal shrink-0 cursor-pointer text-[12px] leading-none font-semibold tracking-[0.5px] uppercase',
            styles.text
          )}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
